import { useEffect, useRef, useState } from "react";
import { createElement } from "react";
import mapboxgl from "mapbox-gl";
import { createRoot } from "react-dom/client";
import { MAP_STYLES } from "@utils/maps/mapStyles";
import { resolveMediaUrl } from "@utils/media/mediaUrl";
import ModePointPopupCard from "@components/mode_tool/ModePointPopupCard";
import "@assets/css/mode_tool/pointPopup.css";
import {
  applyMap3D,
  createMapboxInstance,
  destroyMapboxInstance,
  setMapStyle,
  sync3DWhenStyleReady,
} from "@utils/maps/mapboxHelpers";
import { buildDrivingLabelCode } from "@utils/mode_tool/modeRoute";

const ROUTE_SOURCE_ID = "start-point-route-source";
const ROUTE_LAYER_ID = "start-point-route-layer";
const COMBINED_ROUTE_SOURCE_ID = "all-tour-routes-preview-source";
const COMBINED_ROUTE_DRIVING_LAYER_ID = "all-tour-routes-driving-layer";
const COMBINED_ROUTE_WALKING_LAYER_ID = "all-tour-routes-walking-layer";
const COMBINED_ROUTE_RANDOM_LAYER_ID = "all-tour-routes-random-layer";
const BOUNDARY_SOURCE_ID = "map-boundary-source";
const BOUNDARY_FILL_LAYER_ID = "map-boundary-fill-layer";
const BOUNDARY_LINE_LAYER_ID = "map-boundary-line-layer";
const ROUTE_SHAPE_POINT_SOURCE_ID = "route-shape-point-source";
const ROUTE_SHAPE_POINT_LAYER_ID = "route-shape-point-layer";
const ROUTE_SHAPE_DELETE_BG_LAYER_ID = "route-shape-delete-bg-layer";
const ROUTE_SHAPE_DELETE_TEXT_LAYER_ID = "route-shape-delete-text-layer";
const ROUTE_SHAPE_CLICK_TOLERANCE_PX = 14;
const MEASUREMENT_SOURCE_ID = "map-distance-measurement-source";
const MEASUREMENT_LINE_LAYER_ID = "map-distance-measurement-line";
const MEASUREMENT_POINT_LAYER_ID = "map-distance-measurement-points";
const MEASUREMENT_LABEL_LAYER_ID = "map-distance-measurement-label";

function calculateHaversineDistanceKm(start = {}, end = {}) {
  const startLng = Number(start?.longitude ?? start?.lng);
  const startLat = Number(start?.latitude ?? start?.lat);
  const endLng = Number(end?.longitude ?? end?.lng);
  const endLat = Number(end?.latitude ?? end?.lat);
  if (
    !Number.isFinite(startLng) ||
    !Number.isFinite(startLat) ||
    !Number.isFinite(endLng) ||
    !Number.isFinite(endLat)
  ) {
    return 0;
  }

  const earthRadiusKm = 6371.0088;
  const toRadians = (value) => (value * Math.PI) / 180;
  const latitudeDelta = toRadians(endLat - startLat);
  const longitudeDelta = toRadians(endLng - startLng);
  const startLatitude = toRadians(startLat);
  const endLatitude = toRadians(endLat);
  const haversine =
    Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(startLatitude) *
      Math.cos(endLatitude) *
      Math.sin(longitudeDelta / 2) ** 2;

  return (
    earthRadiusKm *
    2 *
    Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
  );
}

function getRouteGeometryLines(geometry = {}) {
  if (String(geometry?.type || "") === "LineString") {
    return Array.isArray(geometry?.coordinates) ? [geometry.coordinates] : [];
  }
  if (String(geometry?.type || "") === "MultiLineString") {
    return Array.isArray(geometry?.coordinates) ? geometry.coordinates : [];
  }
  return [];
}

function projectPointOnSegment(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy || 1;
  const ratio = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared,
    ),
  );
  return {
    x: start.x + ratio * dx,
    y: start.y + ratio * dy,
    ratio,
  };
}

function indexToAlphaLabel(index = 0) {
  // Converts 0-based indexes into spreadsheet-like labels: 0->A, 25->Z, 26->AA.
  let value = Number(index) + 1;
  if (!Number.isFinite(value) || value < 1) return "A";
  let label = "";
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

function normalizePointType(pointType = "") {
  // Guards point type to the known random-mode values; defaults to building.
  const value = String(pointType || "")
    .trim()
    .toLowerCase();
  if (value === "parking" || value === "restaurant" || value === "hotel")
    return value;
  return "building";
}

function createRouteShapePointElement() {
  const element = document.createElement("div");
  element.className = "walking-route-shape-marker";
  element.title = "Drag to adjust route.";
  const dot = document.createElement("span");
  dot.className = "walking-route-shape-marker-dot";
  element.appendChild(dot);
  return element;
}

function resolveDrivingMarkerLabel(point = {}, fallbackIndex = 0) {
  return buildDrivingLabelCode(point, fallbackIndex);
}

function distance2D(a, b) {
  const dx = Number(a?.lng ?? 0) - Number(b?.lng ?? 0);
  const dy = Number(a?.lat ?? 0) - Number(b?.lat ?? 0);
  return Math.sqrt(dx * dx + dy * dy);
}

function orderBoundaryPointsByShape(rawPoints = [], previousPoints = []) {
  const source = Array.isArray(rawPoints) ? rawPoints : [];
  if (source.length <= 2) return source;

  const byId = new Map(
    source
      .map((point) => [String(point?.id || ""), point])
      .filter(([id]) => id),
  );
  if (!byId.size) return source;

  // Keep existing sequence from previous frame to preserve user-drawn shape.
  const ordered = (Array.isArray(previousPoints) ? previousPoints : [])
    .map((point) => byId.get(String(point?.id || "")))
    .filter(Boolean);

  const remaining = source.filter(
    (point) =>
      !ordered.some(
        (item) => String(item?.id || "") === String(point?.id || ""),
      ),
  );

  if (!ordered.length) {
    return source;
  }

  const insertBetweenBestEdge = (list, point) => {
    if (list.length < 2) {
      list.push(point);
      return list;
    }
    let bestInsertIndex = list.length;
    let bestCost = Number.POSITIVE_INFINITY;
    for (let i = 0; i < list.length; i += 1) {
      const current = list[i];
      const next = list[(i + 1) % list.length];
      const cost =
        distance2D(current, point) +
        distance2D(point, next) -
        distance2D(current, next);
      if (cost < bestCost) {
        bestCost = cost;
        bestInsertIndex = i + 1;
      }
    }
    list.splice(bestInsertIndex, 0, point);
    return list;
  };

  remaining.forEach((point) => {
    insertBetweenBestEdge(ordered, point);
  });

  return ordered;
}

// Centralizes map lifecycle and all marker/popup interactions used by dashboard tools.
function useMapboxDashboard({
  mapboxToken,
  initialStyle,
  isRouteEnabled,
  externalRouteGeometry,
  isBoundaryAutoDrawEnabled,
  isModePointPopupEnabled,
  popupRefreshToken,
  popupLanguageRefreshKey,
  getPointPopupData,
  onPointPopupSave,
  onPointMarkerClick,
  onPointPopupDelete,
  isRouteShapeEditEnabled = false,
  routeShapeMode = "",
  routeShapePoints = [],
  onAddRouteShapePoint,
  onMoveRouteShapePoint,
  onDeleteRouteShapePoint,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const locationMarkerRef = useRef(null);
  const popupRef = useRef(null);
  const popupReactRootRef = useRef(null);
  const popupMarkerRef = useRef(null);
  const popupPointRef = useRef(null);
  const combinedPreviewMetaByIdRef = useRef(new Map());
  const modeStartPointMarkerRef = useRef(null);
  const routeShapeMarkersRef = useRef([]);
  const routeShapePointsRef = useRef([]);
  const routeShapeDeleteHandlersBoundRef = useRef(false);
  const routeShapeDeleteDragRef = useRef(null);
  const routeShapeDeleteSuppressClickRef = useRef(false);
  const isMeasurementEnabledRef = useRef(false);
  const measurementPointsRef = useRef([]);
  const isPointModeRef = useRef(false);
  const isDrawingEnabledRef = useRef(false);
  const isRouteShapeEditEnabledRef = useRef(false);
  const routeShapeModeRef = useRef("");
  const onAddRouteShapePointRef = useRef(null);
  const onMoveRouteShapePointRef = useRef(null);
  const onDeleteRouteShapePointRef = useRef(null);
  const isModePointPopupEnabledRef = useRef(false);
  const isBoundaryAutoDrawEnabledRef = useRef(false);
  const boundaryRenderVersionRef = useRef(0);
  const boundaryRenderTokenRef = useRef(0);
  const styleRestoreTokenRef = useRef(0);
  const styleRestoreTimeoutRef = useRef(null);
  const is3DEnabledRef = useRef(false);
  const isRouteEnabledRef = useRef(false);
  const routeRenderTokenRef = useRef(0);
  const externalRouteSyncLockRef = useRef(false);
  const markerColorRef = useRef("#ef4444");
  const markerVariantRef = useRef("default");
  const routeColorRef = useRef("#38bdf8");
  const routeDashArrayRef = useRef([1, 0]);
  const pointIdCounterRef = useRef(1);
  const pointsRef = useRef([]);
  const externalRouteGeometryRef = useRef(null);
  const latestCombinedPreviewRef = useRef({
    active: false,
    routes: [],
    startPoints: [],
    syncDraftPoints: false,
  });
  const boundaryPointsRef = useRef([]);
  const [selectedStyle, setSelectedStyle] = useState(
    initialStyle || MAP_STYLES[0].value,
  );
  const [is3DEnabled, setIs3DEnabled] = useState(false);
  const [isPointMode, setIsPointMode] = useState(false);
  const [isDrawingEnabled, setIsDrawingEnabled] = useState(false);
  const [routeColor, setRouteColor] = useState("#38bdf8");
  const [routeDashArray, setRouteDashArray] = useState([1, 0]);
  const [points, setPoints] = useState([]);
  const [isMeasurementEnabled, setIsMeasurementEnabled] = useState(false);
  const [measurementPoints, setMeasurementPoints] = useState([]);
  const [measurementDistanceKm, setMeasurementDistanceKm] = useState(null);

  // Refs keep latest toggle values readable from mapbox native listeners.
  useEffect(() => {
    isPointModeRef.current = isPointMode;
  }, [isPointMode]);

  useEffect(() => {
    isRouteShapeEditEnabledRef.current = Boolean(isRouteShapeEditEnabled);
  }, [isRouteShapeEditEnabled]);

  useEffect(() => {
    routeColorRef.current = routeColor;
    routeDashArrayRef.current = routeDashArray;
  }, [routeColor, routeDashArray]);

  useEffect(() => {
    if (!isRouteShapeEditEnabled || !mapRef.current) return undefined;
    const map = mapRef.current;
    const resizeNow = () => map.resize();
    resizeNow();
    const firstTimeoutId = window.setTimeout(resizeNow, 80);
    const secondTimeoutId = window.setTimeout(resizeNow, 220);
    return () => {
      window.clearTimeout(firstTimeoutId);
      window.clearTimeout(secondTimeoutId);
    };
  }, [isRouteShapeEditEnabled]);

  useEffect(() => {
    routeShapeModeRef.current = String(routeShapeMode || "").toLowerCase();
  }, [routeShapeMode]);

  useEffect(() => {
    onAddRouteShapePointRef.current = onAddRouteShapePoint;
  }, [onAddRouteShapePoint]);

  useEffect(() => {
    onMoveRouteShapePointRef.current = onMoveRouteShapePoint;
  }, [onMoveRouteShapePoint]);

  useEffect(() => {
    onDeleteRouteShapePointRef.current = onDeleteRouteShapePoint;
  }, [onDeleteRouteShapePoint]);

  useEffect(() => {
    isDrawingEnabledRef.current = isDrawingEnabled;
  }, [isDrawingEnabled]);

  useEffect(() => {
    isMeasurementEnabledRef.current = isMeasurementEnabled;
    const map = mapRef.current;
    if (map) {
      map.getCanvas().style.cursor = isMeasurementEnabled ? "crosshair" : "";
    }
  }, [isMeasurementEnabled]);

  useEffect(() => {
    is3DEnabledRef.current = is3DEnabled;
  }, [is3DEnabled]);

  useEffect(() => {
    measurementPointsRef.current = measurementPoints;
  }, [measurementPoints]);

  useEffect(() => {
    isModePointPopupEnabledRef.current = isModePointPopupEnabled;
  }, [isModePointPopupEnabled]);

  useEffect(() => {
    isBoundaryAutoDrawEnabledRef.current = Boolean(isBoundaryAutoDrawEnabled);
    if (!isBoundaryAutoDrawEnabled) {
      boundaryRenderVersionRef.current += 1;
      clearBoundaryPolygon();
    }
  }, [isBoundaryAutoDrawEnabled]);

  useEffect(() => {
    isRouteEnabledRef.current = isRouteEnabled;
  }, [isRouteEnabled]);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  useEffect(() => {
    if (hasGeometryCoordinates(externalRouteGeometry)) {
      externalRouteSyncLockRef.current = false;
      externalRouteGeometryRef.current = externalRouteGeometry;
      return;
    }
    if (externalRouteSyncLockRef.current) return;
    externalRouteGeometryRef.current = externalRouteGeometry;
  }, [externalRouteGeometry]);

  function hasGeometryCoordinates(geometry = null) {
    const type = String(geometry?.type || "");
    if (type === "LineString") {
      return (
        Array.isArray(geometry?.coordinates) && geometry.coordinates.length > 0
      );
    }
    if (type === "MultiLineString") {
      return (
        Array.isArray(geometry?.coordinates) &&
        geometry.coordinates.some(
          (lineCoords) => Array.isArray(lineCoords) && lineCoords.length > 0,
        )
      );
    }
    return (
      Array.isArray(geometry?.coordinates) && geometry.coordinates.length > 0
    );
  }

  function getSourceGeometrySnapshot(source) {
    const currentData = source?._data;
    if (currentData?.geometry) return currentData.geometry;
    return null;
  }

  function resolveRouteShapeClickLngLat(event) {
    const map = mapRef.current;
    if (!map || !event?.point) return null;
    if (!map.getLayer(ROUTE_LAYER_ID)) return null;
    const tolerance = ROUTE_SHAPE_CLICK_TOLERANCE_PX;
    const box = [
      [event.point.x - tolerance, event.point.y - tolerance],
      [event.point.x + tolerance, event.point.y + tolerance],
    ];
    const features = map.queryRenderedFeatures(box, {
      layers: [ROUTE_LAYER_ID],
    });
    if (!features.length) return null;
    const routeGeometry =
      getSourceGeometrySnapshot(map.getSource(ROUTE_SOURCE_ID)) ||
      features[0]?.geometry ||
      {};
    const lines = getRouteGeometryLines(routeGeometry);
    if (!lines.length) return null;
    const findNearestRoutePosition = (targetPoint) => {
      let nearestPosition = null;
      let nearestDistance = Number.POSITIVE_INFINITY;
      lines.forEach((lineCoordinates, lineIndex) => {
        const validCoordinates = (Array.isArray(lineCoordinates)
          ? lineCoordinates
          : []
        ).filter(
          (coordinate) =>
            Array.isArray(coordinate) &&
            Number.isFinite(Number(coordinate[0])) &&
            Number.isFinite(Number(coordinate[1])),
        );
        let lineDistance = 0;
        for (let index = 0; index < validCoordinates.length - 1; index += 1) {
          const startLngLat = validCoordinates[index];
          const endLngLat = validCoordinates[index + 1];
          const startPoint = map.project(startLngLat);
          const endPoint = map.project(endLngLat);
          const dx = endPoint.x - startPoint.x;
          const dy = endPoint.y - startPoint.y;
          const segmentLength = Math.sqrt(dx * dx + dy * dy);
          const projected = projectPointOnSegment(
            targetPoint,
            startPoint,
            endPoint,
          );
          const distance =
            (targetPoint.x - projected.x) * (targetPoint.x - projected.x) +
            (targetPoint.y - projected.y) * (targetPoint.y - projected.y);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestPosition = {
              point: projected,
              distance,
              lineIndex,
              segmentIndex: index,
              along: lineDistance + projected.ratio * segmentLength,
            };
          }
          lineDistance += segmentLength;
        }
      });
      return nearestPosition;
    };
    const nearestPosition = findNearestRoutePosition(event.point);
    if (!nearestPosition?.point) return null;
    if (Math.sqrt(nearestPosition.distance) > tolerance) return null;
    const routePointPositions = markersRef.current
      .map((marker, index) => {
        const markerLngLat = marker.getLngLat();
        const position = findNearestRoutePosition(map.project(markerLngLat));
        if (!position) return null;
        return {
          order: index + 1,
          pointId: String(marker?.__pointId || `point-${index + 1}`),
          labelCode:
            String(marker?.__markerLabel || "").toLowerCase() ||
            indexToAlphaLabel(index).toLowerCase(),
          lineIndex: position.lineIndex,
          along: position.along,
          distance: position.distance,
        };
      })
      .filter(
        (item) =>
          item &&
          Number.isFinite(item.along) &&
          Math.sqrt(Number(item.distance || 0)) <= 100,
      )
      .sort((a, b) => {
        if (a.lineIndex !== b.lineIndex) return a.lineIndex - b.lineIndex;
        return a.along - b.along;
      });
    const sameLineRoutePoints = routePointPositions.filter(
      (point) => point.lineIndex === nearestPosition.lineIndex,
    );
    let routeSegmentInfo = null;
    for (let index = 0; index < sameLineRoutePoints.length - 1; index += 1) {
      const start = sameLineRoutePoints[index];
      const end = sameLineRoutePoints[index + 1];
      const minAlong = Math.min(start.along, end.along);
      const maxAlong = Math.max(start.along, end.along);
      if (
        nearestPosition.along >= minAlong - 1 &&
        nearestPosition.along <= maxAlong + 1
      ) {
        const length = Math.max(1, maxAlong - minAlong);
        routeSegmentInfo = {
          afterPointOrder: start.order,
          pointId: start.pointId,
          labelCode: start.labelCode,
          segmentRatio: Math.max(
            0,
            Math.min(1, (nearestPosition.along - minAlong) / length),
          ),
        };
        break;
      }
    }
    if (!routeSegmentInfo && sameLineRoutePoints.length) {
      const previousPoint =
        [...sameLineRoutePoints]
          .reverse()
          .find((point) => point.along <= nearestPosition.along) ||
        sameLineRoutePoints[0];
      routeSegmentInfo = {
        afterPointOrder: previousPoint.order,
        pointId: previousPoint.pointId,
        labelCode: previousPoint.labelCode,
        segmentRatio: 0,
      };
    }
    const lngLat = map.unproject(nearestPosition.point);
    return {
      longitude: Number(lngLat.lng.toFixed(7)),
      latitude: Number(lngLat.lat.toFixed(7)),
      ...(routeSegmentInfo || {}),
    };
  }

  function hasBoundaryFeatures(source) {
    const currentData = source?._data;
    return (
      Array.isArray(currentData?.features) && currentData.features.length > 0
    );
  }

  function ensureRouteLayer() {
    // Route source/layer must exist per style load; styles reset custom layers.
    const map = mapRef.current;
    if (!map) return;
    if (!map.isStyleLoaded()) return;
    if (!map.getSource(ROUTE_SOURCE_ID)) {
      map.addSource(ROUTE_SOURCE_ID, {
        type: "geojson",
        data: {
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: [],
          },
          properties: {},
        },
      });
    }
    if (!map.getLayer(ROUTE_LAYER_ID)) {
      map.addLayer({
        id: ROUTE_LAYER_ID,
        type: "line",
        source: ROUTE_SOURCE_ID,
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": routeColorRef.current,
          "line-width": 7,
          "line-opacity": 0.9,
          "line-dasharray": routeDashArrayRef.current,
        },
      });
    }
  }

  function buildMeasurementGeoJson(rawPoints = measurementPointsRef.current) {
    const normalizedPoints = (Array.isArray(rawPoints) ? rawPoints : [])
      .map((point) => [Number(point?.longitude), Number(point?.latitude)])
      .filter(
        ([longitude, latitude]) =>
          Number.isFinite(longitude) && Number.isFinite(latitude),
      )
      .slice(0, 2);
    const features = normalizedPoints.map((coordinates, index) => ({
      type: "Feature",
      properties: {
        featureType: "point",
        pointLabel: index === 0 ? "A" : "B",
      },
      geometry: { type: "Point", coordinates },
    }));

    if (normalizedPoints.length === 2) {
      const distanceKm = calculateHaversineDistanceKm(
        {
          longitude: normalizedPoints[0][0],
          latitude: normalizedPoints[0][1],
        },
        {
          longitude: normalizedPoints[1][0],
          latitude: normalizedPoints[1][1],
        },
      );
      const distanceLabel =
        distanceKm < 1
          ? `${Math.round(distanceKm * 1000)} m`
          : `${distanceKm.toFixed(distanceKm >= 10 ? 2 : 3)} km`;
      features.unshift({
        type: "Feature",
        properties: { featureType: "line" },
        geometry: { type: "LineString", coordinates: normalizedPoints },
      });
      features.push({
        type: "Feature",
        properties: { featureType: "label", distanceLabel },
        geometry: {
          type: "Point",
          coordinates: [
            (normalizedPoints[0][0] + normalizedPoints[1][0]) / 2,
            (normalizedPoints[0][1] + normalizedPoints[1][1]) / 2,
          ],
        },
      });
    }

    return { type: "FeatureCollection", features };
  }

  function ensureMeasurementLayers() {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (!map.getSource(MEASUREMENT_SOURCE_ID)) {
      map.addSource(MEASUREMENT_SOURCE_ID, {
        type: "geojson",
        data: buildMeasurementGeoJson(),
      });
    }
    if (!map.getLayer(MEASUREMENT_LINE_LAYER_ID)) {
      map.addLayer({
        id: MEASUREMENT_LINE_LAYER_ID,
        type: "line",
        source: MEASUREMENT_SOURCE_ID,
        filter: ["==", ["get", "featureType"], "line"],
        layout: { "line-cap": "round", "line-join": "round" },
        paint: {
          "line-color": "#f59e0b",
          "line-width": 4,
          "line-dasharray": [2, 1.5],
        },
      });
    }
    if (!map.getLayer(MEASUREMENT_POINT_LAYER_ID)) {
      map.addLayer({
        id: MEASUREMENT_POINT_LAYER_ID,
        type: "circle",
        source: MEASUREMENT_SOURCE_ID,
        filter: ["==", ["get", "featureType"], "point"],
        paint: {
          "circle-radius": 7,
          "circle-color": "#f59e0b",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 3,
        },
      });
    }
    if (!map.getLayer(MEASUREMENT_LABEL_LAYER_ID)) {
      map.addLayer({
        id: MEASUREMENT_LABEL_LAYER_ID,
        type: "symbol",
        source: MEASUREMENT_SOURCE_ID,
        filter: ["==", ["get", "featureType"], "label"],
        layout: {
          "text-field": ["get", "distanceLabel"],
          "text-size": 14,
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-offset": [0, -1.2],
          "text-allow-overlap": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#263c60",
          "text-halo-width": 3,
        },
      });
    }
  }

  function syncMeasurementLayers(rawPoints = measurementPointsRef.current) {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    ensureMeasurementLayers();
    map
      .getSource(MEASUREMENT_SOURCE_ID)
      ?.setData(buildMeasurementGeoJson(rawPoints));
  }

  function clearMeasurement() {
    measurementPointsRef.current = [];
    setMeasurementPoints([]);
    setMeasurementDistanceKm(null);
    syncMeasurementLayers([]);
  }

  function toggleMeasurement(nextEnabled) {
    const enabled = Boolean(nextEnabled);
    if (!enabled) {
      clearMeasurement();
    }
    isMeasurementEnabledRef.current = enabled;
    setIsMeasurementEnabled(enabled);
  }

  function handleMeasurementMapClick(event) {
    const clickedPoint = {
      longitude: Number(event?.lngLat?.lng),
      latitude: Number(event?.lngLat?.lat),
    };
    if (
      !Number.isFinite(clickedPoint.longitude) ||
      !Number.isFinite(clickedPoint.latitude)
    ) {
      return;
    }

    const currentPoints = measurementPointsRef.current || [];
    const nextPoints =
      currentPoints.length >= 2 ? [clickedPoint] : [...currentPoints, clickedPoint];
    measurementPointsRef.current = nextPoints;
    setMeasurementPoints(nextPoints);
    if (nextPoints.length === 2) {
      setMeasurementDistanceKm(
        calculateHaversineDistanceKm(nextPoints[0], nextPoints[1]),
      );
    } else {
      setMeasurementDistanceKm(null);
    }
    syncMeasurementLayers(nextPoints);
  }

  function ensureRouteShapePointLayer() {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (!map.getSource(ROUTE_SHAPE_POINT_SOURCE_ID)) {
      map.addSource(ROUTE_SHAPE_POINT_SOURCE_ID, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }
    if (!map.getLayer(ROUTE_SHAPE_POINT_LAYER_ID)) {
      map.addLayer({
        id: ROUTE_SHAPE_POINT_LAYER_ID,
        type: "circle",
        source: ROUTE_SHAPE_POINT_SOURCE_ID,
        paint: {
          "circle-radius": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            4,
            16,
            6,
            20,
            8,
          ],
          "circle-color": "#588354",
          "circle-stroke-color": "#9ff098",
          "circle-stroke-width": 3,
          "circle-opacity": 1,
        },
      });
    }
    if (!map.getLayer(ROUTE_SHAPE_DELETE_BG_LAYER_ID)) {
      map.addLayer({
        id: ROUTE_SHAPE_DELETE_BG_LAYER_ID,
        type: "circle",
        source: ROUTE_SHAPE_POINT_SOURCE_ID,
        paint: {
          "circle-radius": 8,
          "circle-color": "#ef4444",
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 1,
          "circle-translate": [10, 0],
          "circle-translate-anchor": "viewport",
        },
      });
    }
    if (!map.getLayer(ROUTE_SHAPE_DELETE_TEXT_LAYER_ID)) {
      map.addLayer({
        id: ROUTE_SHAPE_DELETE_TEXT_LAYER_ID,
        type: "symbol",
        source: ROUTE_SHAPE_POINT_SOURCE_ID,
        layout: {
          "text-field": "×",
          "text-size": 14,
          "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
          "text-allow-overlap": true,
          "text-ignore-placement": true,
        },
        paint: {
          "text-color": "#ffffff",
          "text-translate": [10, 0],
          "text-translate-anchor": "viewport",
        },
      });
    }
    if (!routeShapeDeleteHandlersBoundRef.current) {
      const handleDeleteClick = (event) => {
        event.preventDefault();
        event.originalEvent?.stopPropagation?.();
        if (routeShapeDeleteSuppressClickRef.current) {
          routeShapeDeleteSuppressClickRef.current = false;
          return;
        }
        const shapePointId = String(event.features?.[0]?.properties?.id || "");
        if (!shapePointId) return;
        onDeleteRouteShapePointRef.current?.(shapePointId);
      };
      const updateShapePointFromDeleteHandle = (shapePointId, point) => {
        const mapPoint = map.unproject([point.x - 10, point.y]);
        const longitude = Number(mapPoint.lng.toFixed(7));
        const latitude = Number(mapPoint.lat.toFixed(7));
        routeShapePointsRef.current = (routeShapePointsRef.current || []).map(
          (shapePoint, shapeIndex) =>
            String(shapePoint?.id || `shape-${shapeIndex + 1}`) ===
            shapePointId
              ? {
                  ...shapePoint,
                  longitude,
                  latitude,
                }
              : shapePoint,
        );
        routeShapeMarkersRef.current.forEach((marker) => {
          if (marker.__shapePointId !== shapePointId) return;
          marker.setLngLat([longitude, latitude]);
        });
        syncRouteShapePointLayer(routeShapePointsRef.current);
        return { longitude, latitude };
      };
      const handleDeleteMouseDown = (event) => {
        event.preventDefault();
        event.originalEvent?.stopPropagation?.();
        const shapePointId = String(event.features?.[0]?.properties?.id || "");
        if (!shapePointId) return;
        routeShapeDeleteDragRef.current = {
          id: shapePointId,
          moved: false,
          lastPosition: null,
          startPoint: event.point,
        };
        map.dragPan.disable();
      };
      const handleDeleteMouseMove = (event) => {
        const dragState = routeShapeDeleteDragRef.current;
        if (!dragState?.id) return;
        const dx = Math.abs(Number(event.point?.x || 0) - Number(dragState.startPoint?.x || 0));
        const dy = Math.abs(Number(event.point?.y || 0) - Number(dragState.startPoint?.y || 0));
        if (dx > 2 || dy > 2) {
          dragState.moved = true;
        }
        dragState.lastPosition = updateShapePointFromDeleteHandle(
          dragState.id,
          event.point,
        );
      };
      const handleDeleteMouseUp = () => {
        const dragState = routeShapeDeleteDragRef.current;
        if (!dragState?.id) return;
        if (dragState.moved && dragState.lastPosition) {
          routeShapeDeleteSuppressClickRef.current = true;
          onMoveRouteShapePointRef.current?.(
            dragState.id,
            dragState.lastPosition,
          );
        }
        routeShapeDeleteDragRef.current = null;
        if (!map.dragPan.isEnabled()) {
          map.dragPan.enable();
        }
      };
      const handleMouseEnter = () => {
        map.getCanvas().style.cursor = "pointer";
      };
      const handleMouseLeave = () => {
        map.getCanvas().style.cursor = "";
      };
      [ROUTE_SHAPE_DELETE_BG_LAYER_ID, ROUTE_SHAPE_DELETE_TEXT_LAYER_ID].forEach(
        (layerId) => {
          map.on("click", layerId, handleDeleteClick);
          map.on("mousedown", layerId, handleDeleteMouseDown);
          map.on("mouseenter", layerId, handleMouseEnter);
          map.on("mouseleave", layerId, handleMouseLeave);
        },
      );
      map.on("mousemove", handleDeleteMouseMove);
      map.on("mouseup", handleDeleteMouseUp);
      routeShapeDeleteHandlersBoundRef.current = true;
    }
    if (map.getLayer(ROUTE_SHAPE_POINT_LAYER_ID)) {
      try {
        map.moveLayer(ROUTE_SHAPE_POINT_LAYER_ID);
      } catch (error) {}
    }
    [ROUTE_SHAPE_DELETE_BG_LAYER_ID, ROUTE_SHAPE_DELETE_TEXT_LAYER_ID].forEach(
      (layerId) => {
        if (!map.getLayer(layerId)) return;
        try {
          map.moveLayer(layerId);
        } catch (error) {}
      },
    );
  }

  function ensureCombinedRoutePreviewLayers() {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (!map.getSource(COMBINED_ROUTE_SOURCE_ID)) {
      map.addSource(COMBINED_ROUTE_SOURCE_ID, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }
    if (!map.getLayer(COMBINED_ROUTE_DRIVING_LAYER_ID)) {
      map.addLayer({
        id: COMBINED_ROUTE_DRIVING_LAYER_ID,
        type: "line",
        source: COMBINED_ROUTE_SOURCE_ID,
        filter: ["==", ["get", "modeType"], "driving"],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#4faede",
          "line-width": 6,
          "line-opacity": 0.9,
          "line-dasharray": [1, 0],
        },
      });
    }
    if (!map.getLayer(COMBINED_ROUTE_WALKING_LAYER_ID)) {
      map.addLayer({
        id: COMBINED_ROUTE_WALKING_LAYER_ID,
        type: "line",
        source: COMBINED_ROUTE_SOURCE_ID,
        filter: ["==", ["get", "modeType"], "walking"],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#588354",
          "line-width": 6,
          "line-opacity": 0.9,
          "line-dasharray": [2, 2],
        },
      });
    }
    if (!map.getLayer(COMBINED_ROUTE_RANDOM_LAYER_ID)) {
      map.addLayer({
        id: COMBINED_ROUTE_RANDOM_LAYER_ID,
        type: "line",
        source: COMBINED_ROUTE_SOURCE_ID,
        filter: ["==", ["get", "modeType"], "random"],
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": "#603d1c",
          "line-width": 6,
          "line-opacity": 0.9,
          "line-dasharray": [1, 0],
        },
      });
    }
  }

  function ensureBoundaryLayers() {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    if (!map.getSource(BOUNDARY_SOURCE_ID)) {
      map.addSource(BOUNDARY_SOURCE_ID, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });
    }
    if (!map.getLayer(BOUNDARY_FILL_LAYER_ID)) {
      map.addLayer({
        id: BOUNDARY_FILL_LAYER_ID,
        type: "fill",
        source: BOUNDARY_SOURCE_ID,
        paint: {
          "fill-color": "#0ea5e9",
          "fill-opacity": 0.18,
        },
      });
    }
    if (!map.getLayer(BOUNDARY_LINE_LAYER_ID)) {
      map.addLayer({
        id: BOUNDARY_LINE_LAYER_ID,
        type: "line",
        source: BOUNDARY_SOURCE_ID,
        paint: {
          "line-color": "#0ea5e9",
          "line-width": 3,
          "line-opacity": 0.95,
        },
      });
    }
  }

  function applyRouteStyleWithRetry(retries = 8) {
    // Styles can be reset while map style loads; retry avoids silent paint misses.
    const map = mapRef.current;
    if (!map) return;
    if (!map.isStyleLoaded()) {
      if (retries <= 0) return;
      window.setTimeout(() => applyRouteStyleWithRetry(retries - 1), 120);
      return;
    }
    ensureRouteLayer();
    if (!map.getLayer(ROUTE_LAYER_ID)) {
      if (retries <= 0) return;
      window.setTimeout(() => applyRouteStyleWithRetry(retries - 1), 120);
      return;
    }
    map.setPaintProperty(
      ROUTE_LAYER_ID,
      "line-color",
      routeColorRef.current,
    );
    map.setPaintProperty(
      ROUTE_LAYER_ID,
      "line-dasharray",
      routeDashArrayRef.current,
    );
  }

  function syncRouteGeometry(nextPoints = []) {
    // Draws line only when route view is enabled; otherwise keeps source empty.
    const map = mapRef.current;
    if (!map) return;
    if (!map.isStyleLoaded()) {
      map.once("styledata", () => {
        syncRouteGeometry(pointsRef.current || []);
      });
      return;
    }
    ensureRouteLayer();
    const source = map.getSource(ROUTE_SOURCE_ID);
    if (!source) return;
    const externalGeometry =
      externalRouteGeometryRef.current &&
      typeof externalRouteGeometryRef.current === "object"
        ? externalRouteGeometryRef.current
        : null;
    const hasExternalCoordinates = Array.isArray(externalGeometry?.coordinates);
    source.setData({
      type: "Feature",
      geometry: {
        type: hasExternalCoordinates
          ? String(externalGeometry?.type || "LineString")
          : "LineString",
        coordinates: hasExternalCoordinates
          ? externalGeometry.coordinates
          : isRouteEnabledRef.current
            ? nextPoints.map((point) => [point.lng, point.lat])
            : [],
      },
      properties: {},
    });
  }

  function clearRouteGeometry() {
    // Clears route line data without removing the layer/source definition.
    externalRouteSyncLockRef.current = false;
    externalRouteGeometryRef.current = null;
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    ensureRouteLayer();
    const source = map.getSource(ROUTE_SOURCE_ID);
    if (!source) return;
    source.setData({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [],
      },
      properties: {},
    });
  }

  function forceDrawExternalRoute(nextGeometry) {
    // Immediately applies externally computed geometry (e.g., Directions API response).
    routeRenderTokenRef.current += 1;
    externalRouteSyncLockRef.current = hasGeometryCoordinates(nextGeometry);
    externalRouteGeometryRef.current = nextGeometry || null;
    syncRouteGeometry(pointsRef.current || []);
  }

  function drawExternalRouteWithRetry(
    nextGeometry,
    retries = 8,
    renderToken = null,
  ) {
    // Retry version for cases when style/layer is not ready at assignment time.
    const activeRenderToken =
      renderToken === null ? routeRenderTokenRef.current + 1 : renderToken;
    if (renderToken === null) {
      routeRenderTokenRef.current = activeRenderToken;
    }
    externalRouteSyncLockRef.current = hasGeometryCoordinates(nextGeometry);
    externalRouteGeometryRef.current = nextGeometry || null;
    const map = mapRef.current;
    if (!map) return;
    if (!map.isStyleLoaded()) {
      if (retries <= 0) return;
      window.setTimeout(
        () =>
          drawExternalRouteWithRetry(
            nextGeometry,
            retries - 1,
            activeRenderToken,
          ),
        140,
      );
      return;
    }
    ensureRouteLayer();
    const source = map.getSource(ROUTE_SOURCE_ID);
    if (!source) {
      if (retries <= 0) return;
      window.setTimeout(
        () =>
          drawExternalRouteWithRetry(
            nextGeometry,
            retries - 1,
            activeRenderToken,
          ),
        140,
      );
      return;
    }
    const geometryType = String(nextGeometry?.type || "LineString");
    const coords = Array.isArray(nextGeometry?.coordinates)
      ? nextGeometry.coordinates
      : [];
    source.setData({
      type: "Feature",
      geometry: {
        type: geometryType,
        coordinates: coords,
      },
      properties: {},
    });
    window.setTimeout(() => {
      if (routeRenderTokenRef.current !== activeRenderToken) return;
      const currentSource = mapRef.current?.getSource?.(ROUTE_SOURCE_ID);
      if (!currentSource) {
        if (retries <= 0) return;
        drawExternalRouteWithRetry(
          nextGeometry,
          retries - 1,
          activeRenderToken,
        );
        return;
      }
      const sourceGeometry = getSourceGeometrySnapshot(currentSource);
      const shouldHaveGeometry = hasGeometryCoordinates(nextGeometry);
      const hasDrawnGeometry = hasGeometryCoordinates(sourceGeometry);
      if (shouldHaveGeometry && !hasDrawnGeometry && retries > 0) {
        drawExternalRouteWithRetry(
          nextGeometry,
          retries - 1,
          activeRenderToken,
        );
      }
    }, 120);
  }

  function getModePointLngLat(point = {}) {
    const lng = Number(point?.lng ?? point?.longitude);
    const lat = Number(point?.lat ?? point?.latitude);
    return Number.isFinite(lng) && Number.isFinite(lat) ? { lng, lat } : null;
  }

  function getNearestModePointInsertIndex(point = {}, orderedPoints = []) {
    const target = getModePointLngLat(point);
    const coordinates = (Array.isArray(orderedPoints) ? orderedPoints : [])
      .map((item, index) => {
        const lngLat = getModePointLngLat(item);
        return lngLat ? { ...lngLat, index } : null;
      })
      .filter(
        (item) =>
          item && Number.isFinite(item.lng) && Number.isFinite(item.lat),
      );
    if (!target || coordinates.length < 2) return coordinates.length;
    let insertIndex = coordinates.length;
    let nearestDistance = Number.POSITIVE_INFINITY;
    for (let index = 0; index < coordinates.length - 1; index += 1) {
      const start = coordinates[index];
      const end = coordinates[index + 1];
      const dx = end.lng - start.lng;
      const dy = end.lat - start.lat;
      const lengthSquared = dx * dx + dy * dy || 1;
      const ratio = Math.max(
        0,
        Math.min(
          1,
          ((target.lng - start.lng) * dx + (target.lat - start.lat) * dy) /
            lengthSquared,
        ),
      );
      const projectedLng = start.lng + ratio * dx;
      const projectedLat = start.lat + ratio * dy;
      const distance =
        (target.lng - projectedLng) * (target.lng - projectedLng) +
        (target.lat - projectedLat) * (target.lat - projectedLat);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        insertIndex = start.index + 1;
      }
    }
    return insertIndex;
  }

  function orderModePointsWithInsertedMarkers(nextPoints = [], previous = []) {
    const previousIds = new Set(
      (Array.isArray(previous) ? previous : []).map((point) =>
        String(point?.id || ""),
      ),
    );
    const byId = new Map(
      (Array.isArray(nextPoints) ? nextPoints : []).map((point) => [
        String(point?.id || ""),
        point,
      ]),
    );
    const ordered = (Array.isArray(previous) ? previous : [])
      .map((point) => byId.get(String(point?.id || "")))
      .filter(Boolean);
    (Array.isArray(nextPoints) ? nextPoints : []).forEach((point) => {
      const pointId = String(point?.id || "");
      if (previousIds.has(pointId)) return;
      if (point?.insertOnRouteLine) {
        const insertIndex = getNearestModePointInsertIndex(point, ordered);
        ordered.splice(insertIndex, 0, point);
        return;
      }
      ordered.push(point);
    });
    return ordered.map((point, index) => ({
      ...point,
      insertOnRouteLine: false,
      order: index + 1,
    }));
  }

  function syncPointsFromMarkers() {
    // Converts live markers into normalized point list for parent/store sync.
    let nextPoints = markersRef.current.map((marker, index) => {
      const lngLat = marker.getLngLat();
      const markerPointId = marker.__pointId || `point-${index + 1}`;
      return {
        id: markerPointId,
        order: index + 1,
        lng: Number(lngLat.lng.toFixed(6)),
        lat: Number(lngLat.lat.toFixed(6)),
        pointType: marker.__pointType || "",
        insertOnRouteLine: Boolean(marker.__insertOnRouteLine),
      };
    });
    if (
      !isBoundaryAutoDrawEnabledRef.current &&
      (markerVariantRef.current === "mode-driving" ||
        markerVariantRef.current === "mode-walking") &&
      Array.isArray(pointsRef.current) &&
      nextPoints.length > pointsRef.current.length
    ) {
      nextPoints = orderModePointsWithInsertedMarkers(
        nextPoints,
        pointsRef.current,
      );
      const markerById = new Map(
        markersRef.current.map((marker) => [
          String(marker?.__pointId || ""),
          marker,
        ]),
      );
      markersRef.current = nextPoints
        .map((point) => markerById.get(String(point?.id || "")))
        .filter(Boolean);
      refreshMarkerVisualLabels();
      nextPoints = nextPoints.map((point, index) => ({
        ...point,
        order: index + 1,
      }));
    }
    if (isBoundaryAutoDrawEnabledRef.current) {
      nextPoints = orderBoundaryPointsByShape(
        nextPoints,
        pointsRef.current || [],
      );
      // Keep internal marker order aligned with ordered points for stable labels/list order.
      const markerById = new Map(
        markersRef.current.map((marker) => [
          String(marker?.__pointId || ""),
          marker,
        ]),
      );
      markersRef.current = nextPoints
        .map((point) => markerById.get(String(point?.id || "")))
        .filter(Boolean);
      refreshMarkerVisualLabels();
      nextPoints = nextPoints.map((point, index) => ({
        ...point,
        order: index + 1,
      }));
    }
    setPoints(nextPoints);
    // Keep boundaries polygon in sync on add/move/remove when boundaries mode is active.
    if (isBoundaryAutoDrawEnabledRef.current) {
      drawBoundaryPolygonFromPoints(nextPoints);
    }
    syncRouteGeometry(nextPoints);
  }

  function normalizeLocalizedForPopup(value) {
    if (!value) return {};
    if (Array.isArray(value)) {
      return value.reduce((acc, item) => {
        if (!item || typeof item !== "object") return acc;
        const entries = Object.entries(item).filter(([key]) => key !== "id");
        if (!entries.length) return acc;
        const [iso, text] = entries[0];
        acc[iso] = { value: String(text || ""), id: String(item?.id || "") };
        return acc;
      }, {});
    }
    if (typeof value === "object") return value;
    return { es: { value: String(value || ""), id: "" } };
  }

  function buildRoutePointPopupMeta(point = {}, fallbackPointType = "") {
    const imageItems = Array.isArray(point?.images)
      ? point.images.map((item) => ({
          ...item,
          url: resolveMediaUrl(String(item?.url || "")),
          mediaType: "image",
        }))
      : [];
    const audioItems = Array.isArray(point?.audios)
      ? point.audios.map((item) => ({
          ...item,
          url: resolveMediaUrl(String(item?.url || "")),
          mediaType: "audio",
        }))
      : [];
    return {
      name: normalizeLocalizedForPopup(point?.name),
      description: normalizeLocalizedForPopup(point?.description),
      latitude: point?.latitude ?? "",
      longitude: point?.longitude ?? "",
      radiusMeters: Number(point?.radius_meters ?? 0) || 0,
      turnCase: Number(point?.turn_case ?? 0) || 0,
      pointType: String(
        point?.pointType || point?.point_type || fallbackPointType || "",
      ),
      multimedia: [...imageItems, ...audioItems],
    };
  }

  function buildStartPointPopupMeta(startPoint = {}) {
    const descriptionValue =
      typeof startPoint?.description === "string"
        ? { es: { value: startPoint.description, id: "" } }
        : normalizeLocalizedForPopup(startPoint?.description || {});
    const nameValue =
      typeof startPoint?.name === "string"
        ? { es: { value: startPoint.name, id: "" } }
        : normalizeLocalizedForPopup(
            startPoint?.name ||
              startPoint?.zoneName ||
              startPoint?.placeName ||
              "",
          );
    return {
      name: nameValue,
      description: descriptionValue,
      latitude: startPoint?.latitude ?? startPoint?.lat ?? "",
      longitude: startPoint?.longitude ?? startPoint?.lng ?? "",
      radiusMeters:
        Number(startPoint?.radius_meters ?? startPoint?.radiusMeters ?? 0) || 0,
      turnCase: Number(startPoint?.turn_case ?? startPoint?.turnCase ?? 0) || 0,
      multimedia: [],
    };
  }

  function createStartPointMarkerElement() {
    // Uses a custom SVG marker so start-point mode has consistent brand styling.
    const element = document.createElement("div");
    element.className = "start-point-map-marker";
    element.innerHTML = `
      <svg viewBox="0 0 24 24" class="start-point-map-marker-icon" aria-hidden="true">
        <path fill="currentColor" d="M5 3a1 1 0 0 1 1 1v16a1 1 0 1 1-2 0V4a1 1 0 0 1 1-1zm3 1h7.2c.95 0 1.67.92 1.38 1.82L15.3 9l1.28 3.18c.29.9-.43 1.82-1.38 1.82H8a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"/>
      </svg>
    `;
    return element;
  }

  function createModeDrivingMarkerElement(label = "") {
    // Driving markers render numeric labels (1, 2, 3...) centered in the pin.
    const element = document.createElement("div");
    element.className = "mode-driving-map-marker";
    const labelSpan = document.createElement("span");
    labelSpan.className = "mode-driving-map-marker-label";
    labelSpan.textContent = String(label || "");
    element.appendChild(labelSpan);
    return element;
  }

  function createModeWalkingMarkerElement(label = "") {
    // Walking markers render alphabetic labels (A, B, C...) centered in the pin.
    const element = document.createElement("div");
    element.className = "mode-walking-map-marker";
    const labelSpan = document.createElement("span");
    labelSpan.className = "mode-walking-map-marker-label";
    labelSpan.textContent = String(label || "");
    element.appendChild(labelSpan);
    return element;
  }

  function createModeRandomMarkerElement(pointType = "") {
    // Builds the random-mode marker element with the icon mapped from pointType.
    const normalizedPointType = normalizePointType(pointType);
    const iconByType = {
      building: `
        <svg viewBox="0 0 24 24" class="mode-random-map-marker-icon" aria-hidden="true" >
          <path fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M4 21h16v-2H4v2zm2-3h3V4H6v14zm5 0h3V7h-3v11zm5 0h2v-9h-2v9z"/>
        </svg>
      `,
      parking: `
        <svg viewBox="0 0 24 24" class="mode-random-map-marker-icon" aria-hidden="true">
          <path fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M7 3h5a5 5 0 1 1 0 10H9v8H7V3zm2 2v6h3a3 3 0 1 0 0-6H9z"/>
        </svg>
      `,
      restaurant: `
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-tools-kitchen-2">
	      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
	      <path d="M19 3v12h-5c-.023 -3.681 .184 -7.406 5 -12m0 12v6h-1v-3m-10 -14v17m-3 -17v3a3 3 0 1 0 6 0v-3" />
      </svg>  
      `,
      hotel: `
        <svg viewBox="0 0 24 24" class="mode-random-map-marker-icon" aria-hidden="true">
          <path fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M5 2h2v8h10V2h2v20h-2v-6H7v6H5V2zm2 10v2h10v-2H7z"/>
        </svg>
      `,
    };
    const element = document.createElement("div");
    element.className = "mode-random-map-marker";
    element.dataset.pointType = normalizedPointType;
    element.innerHTML = iconByType[normalizedPointType] || iconByType.building;
    return element;
  }

  function syncRandomMarkerIconsFromList(rawPoints = []) {
    // Updates existing random markers in place when pointType changes (no full marker rebuild).
    if (markerVariantRef.current !== "mode-random") return;
    const pointTypeById = new Map();
    (Array.isArray(rawPoints) ? rawPoints : []).forEach((point) => {
      const pointId = String(point?.id || "");
      if (!pointId) return;
      pointTypeById.set(
        pointId,
        normalizePointType(point?.pointType ?? point?.point_type ?? ""),
      );
    });
    markersRef.current.forEach((marker) => {
      const markerPointId = String(marker?.__pointId || "");
      if (!markerPointId) return;
      const nextPointType = pointTypeById.get(markerPointId) || "building";
      if (String(marker?.__pointType || "") === nextPointType) return;
      const markerEl = marker.getElement?.();
      if (!markerEl) return;
      const nextMarkerEl = createModeRandomMarkerElement(nextPointType);
      markerEl.className = nextMarkerEl.className;
      markerEl.innerHTML = nextMarkerEl.innerHTML;
      markerEl.dataset.pointType = nextPointType;
      marker.__pointType = nextPointType;
    });
  }

  function refreshMarkerVisualLabels() {
    // Recomputes visible marker labels after add/remove/reorder operations.
    if (
      markerVariantRef.current !== "mode-driving" &&
      markerVariantRef.current !== "mode-walking"
    )
      return;
    markersRef.current.forEach((marker, index) => {
      const markerEl = marker?.getElement?.();
      const labelEl =
        markerEl?.querySelector?.(".mode-driving-map-marker-label") ||
        markerEl?.querySelector?.(".mode-walking-map-marker-label");
      if (!labelEl) return;
      if (markerVariantRef.current === "mode-walking") {
        labelEl.textContent = indexToAlphaLabel(index);
        return;
      }
      labelEl.textContent = String(marker?.__markerLabel || index + 1);
    });
  }

  function syncDrivingMarkerLabelsFromList(rawPoints = []) {
    if (markerVariantRef.current !== "mode-driving") return;
    const labelById = new Map();
    (Array.isArray(rawPoints) ? rawPoints : []).forEach((point, index) => {
      const pointId = String(point?.id || "");
      if (!pointId) return;
      labelById.set(pointId, resolveDrivingMarkerLabel(point, index));
    });
    markersRef.current.forEach((marker, index) => {
      const markerPointId = String(marker?.__pointId || "");
      if (!markerPointId) return;
      const nextLabel = labelById.get(markerPointId) || String(index + 1);
      marker.__markerLabel = nextLabel;
      const markerEl = marker?.getElement?.();
      const labelEl = markerEl?.querySelector?.(
        ".mode-driving-map-marker-label",
      );
      if (!labelEl) return;
      labelEl.textContent = String(nextLabel);
    });
  }

  function createPointMarker({
    lngLat,
    pointId,
    pointType,
    markerVariant,
    markerLabel,
    draggable = true,
  }) {
    // Marker shape/style is selected by current variant to support each tool mode.
    const activeVariant = String(
      markerVariant || markerVariantRef.current || "default",
    );
    const isStartPointMarker = activeVariant === "start-point";
    const isModeDrivingMarker = activeVariant === "mode-driving";
    const isModeWalkingMarker = activeVariant === "mode-walking";
    const isModeRandomMarker = activeVariant === "mode-random";
    const marker = isStartPointMarker
      ? new mapboxgl.Marker({
          draggable,
          element: createStartPointMarkerElement(),
        })
      : isModeDrivingMarker
        ? new mapboxgl.Marker({
            draggable,
            element: createModeDrivingMarkerElement(markerLabel),
          })
        : isModeWalkingMarker
          ? new mapboxgl.Marker({
              draggable,
              element: createModeWalkingMarkerElement(markerLabel),
            })
          : isModeRandomMarker
            ? new mapboxgl.Marker({
                draggable,
                element: createModeRandomMarkerElement(pointType),
              })
            : new mapboxgl.Marker({
                draggable,
                color: markerColorRef.current,
              });
    marker.setLngLat(lngLat).addTo(mapRef.current);
    marker.__pointId = pointId;
    marker.__pointType = normalizePointType(pointType);
    return marker;
  }

  function clearRouteShapeMarkers() {
    routeShapeMarkersRef.current.forEach((marker) => marker.remove());
    routeShapeMarkersRef.current = [];
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const source = map.getSource(ROUTE_SHAPE_POINT_SOURCE_ID);
    if (!source) return;
    source.setData({
      type: "FeatureCollection",
      features: [],
    });
  }

  function syncRouteShapePointLayer(rawShapePoints = [], retries = 6) {
    const map = mapRef.current;
    if (!map) return;
    if (!map.isStyleLoaded()) {
      if (retries <= 0) return;
      window.setTimeout(
        () => syncRouteShapePointLayer(rawShapePoints, retries - 1),
        120,
      );
      return;
    }
    ensureRouteShapePointLayer();
    const source = map.getSource(ROUTE_SHAPE_POINT_SOURCE_ID);
    if (!source) {
      if (retries <= 0) return;
      window.setTimeout(
        () => syncRouteShapePointLayer(rawShapePoints, retries - 1),
        120,
      );
      return;
    }
    const features = (Array.isArray(rawShapePoints) ? rawShapePoints : [])
      .map((point, index) => {
        const lng = Number(point?.lng ?? point?.longitude);
        const lat = Number(point?.lat ?? point?.latitude);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: [lng, lat],
          },
          properties: {
            id: String(point?.id || `shape-${index + 1}`),
          },
        };
      })
      .filter(Boolean);
    source.setData({
      type: "FeatureCollection",
      features,
    });
  }

  function syncRouteShapePointMarkers(rawShapePoints = []) {
    const map = mapRef.current;
    if (!map) return;
    routeShapePointsRef.current = Array.isArray(rawShapePoints)
      ? rawShapePoints
      : [];
    clearRouteShapeMarkers();
    syncRouteShapePointLayer(rawShapePoints);
    const shapePoints = Array.isArray(rawShapePoints) ? rawShapePoints : [];
    shapePoints.forEach((point, index) => {
      const lng = Number(point?.lng ?? point?.longitude);
      const lat = Number(point?.lat ?? point?.latitude);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      const marker = new mapboxgl.Marker({
        anchor: "center",
        draggable: true,
        element: createRouteShapePointElement(),
        offset: [0, 0],
      })
        .setLngLat([lng, lat])
        .addTo(map);
      marker.__shapePointId = String(point?.id || `shape-${index + 1}`);
      marker.on("drag", () => {
        const nextLngLat = marker.getLngLat();
        routeShapePointsRef.current = (routeShapePointsRef.current || []).map(
          (shapePoint, shapeIndex) =>
            String(shapePoint?.id || `shape-${shapeIndex + 1}`) ===
            marker.__shapePointId
              ? {
                  ...shapePoint,
                  longitude: Number(nextLngLat.lng.toFixed(7)),
                  latitude: Number(nextLngLat.lat.toFixed(7)),
                }
              : shapePoint,
        );
        syncRouteShapePointLayer(routeShapePointsRef.current);
      });
      marker.on("dragend", () => {
        const nextLngLat = marker.getLngLat();
        onMoveRouteShapePointRef.current?.(marker.__shapePointId, {
          longitude: Number(nextLngLat.lng.toFixed(7)),
          latitude: Number(nextLngLat.lat.toFixed(7)),
        });
      });
      marker.getElement().addEventListener("click", (event) => {
        event.stopPropagation();
      });
      marker.getElement().addEventListener("dblclick", (event) => {
        event.stopPropagation();
      });
      routeShapeMarkersRef.current.push(marker);
    });
  }
  function addPointMarker(lngLat, options = {}) {
    // Adds one draggable marker and immediately syncs it to hook state + optional popup.
    if (!mapRef.current) return;
    const pointId = `point-${pointIdCounterRef.current++}`;
    const marker = createPointMarker({
      lngLat,
      pointId,
      pointType: markerVariantRef.current === "mode-random" ? "building" : "",
    });
    marker.__insertOnRouteLine = Boolean(options?.insertOnRouteLine);
    marker.getElement().addEventListener("click", (event) => {
      event.stopPropagation();
      const markerIndex = markersRef.current.indexOf(marker);
      if (markerIndex < 0) return;
      const currentLngLat = marker.getLngLat();
      const point = {
        id: marker.__pointId || `point-${markerIndex + 1}`,
        order: markerIndex + 1,
        lng: Number(currentLngLat.lng.toFixed(6)),
        lat: Number(currentLngLat.lat.toFixed(6)),
      };
      if (!isModePointPopupEnabledRef.current) return;
      // Keep marker selection active while the metadata popup remains disabled.
      onPointMarkerClick?.(point);
      // openPointPopup(marker, point);
    });
    marker.on("drag", syncPointsFromMarkers);
    marker.on("dragend", syncPointsFromMarkers);
    markersRef.current.push(marker);
    refreshMarkerVisualLabels();
    syncPointsFromMarkers();
    const markerIndex = markersRef.current.indexOf(marker);
    const point = {
      id: marker.__pointId || `point-${markerIndex + 1}`,
      order: markerIndex + 1,
      lng: Number(lngLat.lng.toFixed(6)),
      lat: Number(lngLat.lat.toFixed(6)),
    };
    if (isModePointPopupEnabledRef.current) {
      onPointMarkerClick?.(point);
      // openPointPopup(marker, point);
    }
  }

  function closePopupAndCleanup() {
    // Unmounts React popup content to avoid memory leaks between marker selections.
    if (popupReactRootRef.current) {
      popupReactRootRef.current.unmount();
      popupReactRootRef.current = null;
    }
    if (popupRef.current) {
      popupRef.current.remove();
      popupRef.current = null;
    }
    popupMarkerRef.current = null;
    popupPointRef.current = null;
  }

  function openPointPopup(marker, point) {
    // Popup is rendered via React for richer card UI (slider/audio preview).
    const map = mapRef.current;
    if (!map || !marker) return;
    closePopupAndCleanup();
    const previewMeta =
      combinedPreviewMetaByIdRef.current.get(String(point?.id || "")) || null;
    const liveMeta = getPointPopupData?.(point?.id) || null;
    const meta = {
      ...(previewMeta || {}),
      ...(liveMeta || {}),
    };
    const container = document.createElement("div");
    container.className = "mode-point-popup-react";
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: "mode-point-popup-shell",
      offset: {
        top: [0, 20],
        "top-left": [0, 20],
        "top-right": [0, 20],
        bottom: [0, -20],
        "bottom-left": [0, -20],
        "bottom-right": [0, -20],
        left: [10, 0],
        right: [-10, 0],
      },
    })
      .setDOMContent(container)
      .setLngLat(marker.getLngLat())
      .addTo(map);
    popupRef.current = popup;
    popupMarkerRef.current = marker;
    popupPointRef.current = point;
    const root = createRoot(container);
    popupReactRootRef.current = root;
    root.render(
      createElement(ModePointPopupCard, {
        point,
        meta,
        onClose: closePopupAndCleanup,
      }),
    );
  }

  function setPointsFromList(rawPoints = [], options = {}) {
    // Rebuilds marker set from incoming data (driving tour points or selected mode points).
    if (!mapRef.current) return;
    if (options?.markerColor) {
      markerColorRef.current = options.markerColor;
    }
    if (options?.markerVariant) {
      markerVariantRef.current = options.markerVariant;
    }
    combinedPreviewMetaByIdRef.current = new Map();
    clearCombinedRoutePreview();
    if (modeStartPointMarkerRef.current) {
      modeStartPointMarkerRef.current.remove();
      modeStartPointMarkerRef.current = null;
    }
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    clearRouteShapeMarkers();
    const boundsPoints = [];

    let maxPointNumber = 0;
    rawPoints.forEach((point) => {
      const lng = Number(point?.lng ?? point?.longitude);
      const lat = Number(point?.lat ?? point?.latitude);
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      boundsPoints.push({
        id: String(point?.id || `point-${boundsPoints.length + 1}`),
        lng,
        lat,
      });
      const rawPointId = point?.id || `point-${pointIdCounterRef.current++}`;
      const numericId = Number(String(rawPointId).replace("point-", ""));
      if (Number.isFinite(numericId) && numericId > maxPointNumber) {
        maxPointNumber = numericId;
      }
      const marker = createPointMarker({
        lngLat: [lng, lat],
        pointId: rawPointId,
        pointType: point?.pointType ?? point?.point_type ?? "",
        markerLabel:
          markerVariantRef.current === "mode-driving"
            ? resolveDrivingMarkerLabel(point, markersRef.current.length)
            : undefined,
      });
      marker.getElement().addEventListener("click", (event) => {
        event.stopPropagation();
        const markerIndex = markersRef.current.indexOf(marker);
        if (markerIndex < 0) return;
        const currentLngLat = marker.getLngLat();
        const point = {
          id: marker.__pointId || `point-${markerIndex + 1}`,
          order: markerIndex + 1,
          lng: Number(currentLngLat.lng.toFixed(6)),
          lat: Number(currentLngLat.lat.toFixed(6)),
        };
        if (!isModePointPopupEnabledRef.current) return;
        onPointMarkerClick?.(point);
        // openPointPopup(marker, point);
      });
      marker.on("drag", syncPointsFromMarkers);
      marker.on("dragend", syncPointsFromMarkers);
      markersRef.current.push(marker);
    });
    refreshMarkerVisualLabels();
    const startPointMarkerData = options?.startPointMarker || null;
    if (startPointMarkerData) {
      const startLng = Number(
        startPointMarkerData?.lng ?? startPointMarkerData?.longitude,
      );
      const startLat = Number(
        startPointMarkerData?.lat ?? startPointMarkerData?.latitude,
      );
      if (Number.isFinite(startLng) && Number.isFinite(startLat)) {
        if (options?.includeStartPointInBounds) {
          boundsPoints.push({
            id: `selected-start-point-${startPointMarkerData?.id || "current"}`,
            lng: startLng,
            lat: startLat,
          });
        }
        modeStartPointMarkerRef.current = createPointMarker({
          lngLat: [startLng, startLat],
          pointId: `selected-start-point-${startPointMarkerData?.id || "current"}`,
          markerVariant: "start-point",
          draggable: false,
        });
      }
    }
    // Ensures new manual points continue sequence after backend-loaded ids.
    pointIdCounterRef.current = Math.max(
      pointIdCounterRef.current,
      maxPointNumber + 1,
    );
    syncPointsFromMarkers();
    if (options?.autoFitBounds && boundsPoints.length) {
      focusPointsBounds(boundsPoints, {
        padding: Number(options?.fitPadding || 100),
        maxZoom: Number(options?.fitMaxZoom || 13),
        zoom: Number(options?.fitZoom || 13),
        duration: Number(options?.fitDuration || 900),
        withMarker: false,
      });
    }
  }

  function clearAllPoints() {
    // Full marker reset used on tool close/context changes.
    closePopupAndCleanup();
    if (modeStartPointMarkerRef.current) {
      modeStartPointMarkerRef.current.remove();
      modeStartPointMarkerRef.current = null;
    }
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
    combinedPreviewMetaByIdRef.current = new Map();
    clearCombinedRoutePreview();
    setPoints([]);
    syncRouteGeometry([]);
  }

  function clearCombinedRoutePreview() {
    latestCombinedPreviewRef.current = {
      active: false,
      routes: [],
      startPoints: [],
      syncDraftPoints: false,
    };
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const source = map.getSource(COMBINED_ROUTE_SOURCE_ID);
    if (source) {
      source.setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  }

  function clearBoundaryPolygon() {
    boundaryPointsRef.current = [];
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const source = map.getSource(BOUNDARY_SOURCE_ID);
    if (!source) return;
    source.setData({
      type: "FeatureCollection",
      features: [],
    });
  }

  function drawBoundaryPolygonFromPoints(
    rawPoints = [],
    retries = 8,
    renderToken = null,
  ) {
    boundaryPointsRef.current = Array.isArray(rawPoints)
      ? rawPoints.map((point) => ({ ...point }))
      : [];
    const map = mapRef.current;
    if (!map) return;
    boundaryRenderVersionRef.current += 1;
    const renderVersion = boundaryRenderVersionRef.current;
    const activeRenderToken =
      renderToken === null ? boundaryRenderTokenRef.current + 1 : renderToken;
    if (renderToken === null) {
      boundaryRenderTokenRef.current = activeRenderToken;
    }
    const render = () => {
      if (renderVersion !== boundaryRenderVersionRef.current) return;
      ensureBoundaryLayers();
      const source = map.getSource(BOUNDARY_SOURCE_ID);
      if (!source) {
        if (retries <= 0) return;
        window.setTimeout(
          () =>
            drawBoundaryPolygonFromPoints(
              rawPoints,
              retries - 1,
              activeRenderToken,
            ),
          140,
        );
        return;
      }
      const ring = (Array.isArray(rawPoints) ? rawPoints : [])
        .map((point) => {
          const lng = Number(point?.lng ?? point?.longitude);
          const lat = Number(point?.lat ?? point?.latitude);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
          return [lng, lat];
        })
        .filter(Boolean);
      if (ring.length < 3) {
        source.setData({
          type: "FeatureCollection",
          features: [],
        });
        return;
      }
      const first = ring[0];
      const last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        ring.push([first[0], first[1]]);
      }
      source.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: [ring],
            },
            properties: {},
          },
        ],
      });
      window.setTimeout(() => {
        if (boundaryRenderTokenRef.current !== activeRenderToken) return;
        const currentSource = mapRef.current?.getSource?.(BOUNDARY_SOURCE_ID);
        if (!currentSource) {
          if (retries <= 0) return;
          drawBoundaryPolygonFromPoints(
            rawPoints,
            retries - 1,
            activeRenderToken,
          );
          return;
        }
        if (!hasBoundaryFeatures(currentSource) && retries > 0) {
          drawBoundaryPolygonFromPoints(
            rawPoints,
            retries - 1,
            activeRenderToken,
          );
        }
      }, 120);
    };
    if (!map.isStyleLoaded()) {
      map.once("styledata", render);
      return;
    }
    render();
  }

  function normalizeRawPayload(rawValue) {
    if (!rawValue) return null;
    if (typeof rawValue === "string") {
      try {
        return JSON.parse(rawValue);
      } catch (error) {
        return null;
      }
    }
    if (typeof rawValue === "object") return rawValue;
    return null;
  }

  function extractRouteGeometry(routeItem = {}) {
    const raw = normalizeRawPayload(routeItem?.directiosApiRoutePointRaw);
    const normalizedRoutes =
      raw?.routes &&
      typeof raw.routes === "object" &&
      !Array.isArray(raw.routes)
        ? Object.entries(raw.routes).filter(([, routeItem]) =>
            Array.isArray(routeItem?.geometry?.coordinates),
          )
        : [];
    const groupedCoordinates = Array.isArray(raw?.groups)
      ? raw.groups
          .map((group) => group?.geometry?.coordinates)
          .filter((coordinates) => Array.isArray(coordinates))
      : [];
    const geometry =
      (normalizedRoutes.length > 1
        ? {
            type: "MultiLineString",
            coordinates: normalizedRoutes.map(
              ([, routeValue]) => routeValue.geometry.coordinates,
            ),
          }
        : normalizedRoutes.length === 1
          ? normalizedRoutes[0][1]?.geometry || null
          : null) ||
      raw?.routes?.main?.geometry ||
      raw?.geometry ||
      (groupedCoordinates.length > 1
        ? {
            type: "MultiLineString",
            coordinates: groupedCoordinates,
          }
        : groupedCoordinates.length === 1
          ? {
              type: "LineString",
              coordinates: groupedCoordinates[0],
            }
          : null) ||
      raw?.routes?.[0]?.geometry ||
      routeItem?.geometry ||
      null;
    if (!geometry?.type || !Array.isArray(geometry?.coordinates)) return null;
    return geometry;
  }

  function drawCombinedRoutesPreview({
    routes = [],
    startPoints = [],
    syncDraftPoints = true,
  } = {}) {
    latestCombinedPreviewRef.current = {
      active: true,
      routes: Array.isArray(routes) ? routes : [],
      startPoints: Array.isArray(startPoints) ? startPoints : [],
      syncDraftPoints: Boolean(syncDraftPoints),
    };
    const map = mapRef.current;
    if (!map) return;
    const render = () => {
      const orderedRoutes = [...(Array.isArray(routes) ? routes : [])].sort(
        (left, right) => {
          const routePriority = {
            random: 0,
            walking: 1,
            driving: 2,
          };
          const leftType = String(left?.type || "").toLowerCase();
          const rightType = String(right?.type || "").toLowerCase();
          const leftPriority = Object.prototype.hasOwnProperty.call(
            routePriority,
            leftType,
          )
            ? routePriority[leftType]
            : 99;
          const rightPriority = Object.prototype.hasOwnProperty.call(
            routePriority,
            rightType,
          )
            ? routePriority[rightType]
            : 99;
          if (leftPriority !== rightPriority) {
            return leftPriority - rightPriority;
          }
          return 0;
        },
      );
      ensureCombinedRoutePreviewLayers();
      clearRouteGeometry();
      externalRouteGeometryRef.current = null;
      combinedPreviewMetaByIdRef.current = new Map();
      if (modeStartPointMarkerRef.current) {
        modeStartPointMarkerRef.current.remove();
        modeStartPointMarkerRef.current = null;
      }
      markersRef.current.forEach((marker) => marker.remove());
      markersRef.current = [];

      const features = [];
      const previewPoints = [];
      orderedRoutes.forEach((routeItem, routeIndex) => {
        const modeType = String(routeItem?.type || "").toLowerCase();
        const geometry = extractRouteGeometry(routeItem);
        if (geometry) {
          if (String(geometry.type) === "LineString") {
            features.push({
              type: "Feature",
              geometry,
              properties: { modeType: modeType || "driving" },
            });
          } else if (String(geometry.type) === "MultiLineString") {
            (geometry.coordinates || []).forEach((lineCoords) => {
              if (!Array.isArray(lineCoords)) return;
              features.push({
                type: "Feature",
                geometry: { type: "LineString", coordinates: lineCoords },
                properties: { modeType: modeType || "driving" },
              });
            });
          }
        }

        const points = Array.isArray(routeItem?.points) ? routeItem.points : [];
        points.forEach((point, pointIndex) => {
          const lng = Number(point?.longitude ?? point?.lng);
          const lat = Number(point?.latitude ?? point?.lat);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
          const isWalking = modeType === "walking";
          const isRandom = modeType === "random";
          const variant = isWalking
            ? "mode-walking"
            : isRandom
              ? "mode-random"
              : "mode-driving";
          const label = isWalking
            ? indexToAlphaLabel(pointIndex)
            : resolveDrivingMarkerLabel(point, pointIndex);
          const marker = createPointMarker({
            lngLat: [lng, lat],
            pointId: `${routeItem?.startRouteTypeId || routeIndex}-${point?.id || pointIndex + 1}`,
            pointType: isRandom
              ? String(
                  point?.pointType ||
                    point?.point_type ||
                    routeItem?.pointType ||
                    "building",
                )
              : "",
            markerVariant: variant,
            markerLabel: label,
            draggable: false,
          });
          marker.getElement().addEventListener("click", (event) => {
            event.stopPropagation();
            const markerIndex = markersRef.current.indexOf(marker);
            if (markerIndex < 0) return;
            const currentLngLat = marker.getLngLat();
            const markerPoint = {
              id: marker.__pointId || `point-${markerIndex + 1}`,
              order: markerIndex + 1,
              lng: Number(currentLngLat.lng.toFixed(6)),
              lat: Number(currentLngLat.lat.toFixed(6)),
            };
            onPointMarkerClick?.(markerPoint);
            // openPointPopup(marker, markerPoint);
          });
          markersRef.current.push(marker);
          combinedPreviewMetaByIdRef.current.set(
            marker.__pointId,
            buildRoutePointPopupMeta(
              point,
              isRandom ? routeItem?.pointType : modeType,
            ),
          );
          previewPoints.push({
            id: marker.__pointId,
            order: previewPoints.length + 1,
            lng: Number(lng.toFixed(6)),
            lat: Number(lat.toFixed(6)),
            pointType: marker.__pointType || "",
          });
        });
      });

      (Array.isArray(startPoints) ? startPoints : []).forEach(
        (startPoint, index) => {
          const lng = Number(startPoint?.longitude ?? startPoint?.lng);
          const lat = Number(startPoint?.latitude ?? startPoint?.lat);
          if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
          const marker = createPointMarker({
            lngLat: [lng, lat],
            pointId: `start-point-${startPoint?.id || index + 1}`,
            pointType: "",
            markerVariant: "start-point",
            draggable: false,
          });
          marker.getElement().addEventListener("click", (event) => {
            event.stopPropagation();
            const markerIndex = markersRef.current.indexOf(marker);
            if (markerIndex < 0) return;
            const currentLngLat = marker.getLngLat();
            const markerPoint = {
              id: marker.__pointId || `point-${markerIndex + 1}`,
              order: markerIndex + 1,
              lng: Number(currentLngLat.lng.toFixed(6)),
              lat: Number(currentLngLat.lat.toFixed(6)),
            };
            onPointMarkerClick?.(markerPoint);
            // openPointPopup(marker, markerPoint);
          });
          markersRef.current.push(marker);
          combinedPreviewMetaByIdRef.current.set(
            marker.__pointId,
            buildStartPointPopupMeta(startPoint),
          );
          previewPoints.push({
            id: marker.__pointId,
            order: previewPoints.length + 1,
            lng: Number(lng.toFixed(6)),
            lat: Number(lat.toFixed(6)),
            pointType: "",
          });
        },
      );

      const source = map.getSource(COMBINED_ROUTE_SOURCE_ID);
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features,
        });
      }
      markerVariantRef.current = "default";
      if (syncDraftPoints) {
        setPoints(previewPoints);
      }

      const geometryPreviewPoints =
        previewPoints.length > 0
          ? previewPoints
          : features.flatMap((feature, featureIndex) => {
              const coords = Array.isArray(feature?.geometry?.coordinates)
                ? feature.geometry.coordinates
                : [];
              return coords
                .map((coord, coordIndex) => {
                  const lng = Number(coord?.[0]);
                  const lat = Number(coord?.[1]);
                  if (!Number.isFinite(lng) || !Number.isFinite(lat))
                    return null;
                  return {
                    id: `preview-${featureIndex}-${coordIndex}`,
                    lng,
                    lat,
                  };
                })
                .filter(Boolean);
            });

      if (geometryPreviewPoints.length) {
        focusPointsBounds(geometryPreviewPoints, {
          padding: {
            top: 100,
            right: 100,
            bottom: 100,
            left: 440,
          },
          maxZoom: 13,
          zoom: 13,
          duration: 900,
          withMarker: false,
        });
      }
    };

    if (!map.isStyleLoaded()) {
      map.once("styledata", render);
      return;
    }
    render();
  }

  function closeActivePointPopup() {
    // External helper for parent components to close popup explicitly.
    closePopupAndCleanup();
  }

  function setMarkerColor(nextColor = "") {
    // Repaints current marker set with a new color while preserving coordinates.
    if (!nextColor) return;
    markerColorRef.current = nextColor;
    const currentPoints = markersRef.current.map((marker) => {
      const lngLat = marker.getLngLat();
      return {
        lng: Number(lngLat.lng.toFixed(6)),
        lat: Number(lngLat.lat.toFixed(6)),
        pointType: marker.__pointType || "",
      };
    });
    setPointsFromList(currentPoints, {
      markerColor: nextColor,
      markerVariant: markerVariantRef.current,
    });
  }

  function setMarkerVariant(nextVariant = "") {
    // Variant switch only updates future marker creations/rebuilds.
    if (!nextVariant) return;
    markerVariantRef.current = nextVariant;
  }

  function removePointByIndex(index) {
    // Removes point by visible order index and normalizes remaining point order.
    const targetIndex = Number(index);
    if (
      !Number.isInteger(targetIndex) ||
      targetIndex < 0 ||
      targetIndex >= markersRef.current.length
    )
      return;
    const marker = markersRef.current[targetIndex];
    if (marker) marker.remove();
    markersRef.current.splice(targetIndex, 1);
    syncPointsFromMarkers();
  }

  function removePointById(pointId) {
    // Removes point by stable id used by Redux/editor state.
    const targetId = String(pointId || "");
    if (!targetId) return;
    const markerIndex = markersRef.current.findIndex(
      (marker) => String(marker?.__pointId || "") === targetId,
    );
    if (markerIndex < 0) return;
    const marker = markersRef.current[markerIndex];
    if (marker) marker.remove();
    markersRef.current.splice(markerIndex, 1);
    syncPointsFromMarkers();
  }

  function focusPointById(pointId, options = {}) {
    // Camera focus helper used by accordion/list selections.
    const targetId = String(pointId || "");
    if (!targetId || !mapRef.current) return;
    const marker = markersRef.current.find(
      (item) => String(item?.__pointId || "") === targetId,
    );
    if (!marker) return;
    const lngLat = marker.getLngLat();
    mapRef.current.flyTo({
      center: [lngLat.lng, lngLat.lat],
      zoom: options?.zoom || Math.max(mapRef.current.getZoom(), 17),
    });
    if (options?.openPopup && isModePointPopupEnabledRef.current) {
      const markerIndex = markersRef.current.indexOf(marker);
      const point = {
        id: marker.__pointId || `point-${markerIndex + 1}`,
        order: markerIndex + 1,
        lng: Number(lngLat.lng.toFixed(6)),
        lat: Number(lngLat.lat.toFixed(6)),
      };
      onPointMarkerClick?.(point);
      // openPointPopup(marker, point);
    }
  }

  function clearLocationMarker() {
    // Clears temporary location-preview marker used outside point-editing flows.
    if (!locationMarkerRef.current) return;
    locationMarkerRef.current.remove();
    locationMarkerRef.current = null;
  }

  function focusLocation({
    lng,
    lat,
    zoom = 10,
    withMarker = true,
    offset = [0, 0],
  }) {
    // Generic camera move with optional temporary marker.
    if (!mapRef.current || !Number.isFinite(lng) || !Number.isFinite(lat))
      return;
    mapRef.current.flyTo({
      center: [lng, lat],
      zoom,
      offset: Array.isArray(offset) ? offset : [0, 0],
    });

    if (!withMarker) {
      clearLocationMarker();
      return;
    }

    if (!locationMarkerRef.current) {
      locationMarkerRef.current = new mapboxgl.Marker()
        .setLngLat([lng, lat])
        .addTo(mapRef.current);
      return;
    }
    locationMarkerRef.current.setLngLat([lng, lat]);
  }

  function focusPointsBounds(rawPoints = [], options = {}) {
    // Fits the camera to a collection of map points, with single-point fallback.
    const map = mapRef.current;
    const list = Array.isArray(rawPoints) ? rawPoints : [];
    if (!map || !list.length) return;
    const coordinates = list
      .map((point) => {
        const lng = Number(point?.lng ?? point?.longitude);
        const lat = Number(point?.lat ?? point?.latitude);
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
        return [lng, lat];
      })
      .filter(Boolean);
    if (!coordinates.length) return;
    if (coordinates.length === 1) {
      const [lng, lat] = coordinates[0];
      focusLocation({
        lng,
        lat,
        zoom: Number(options?.zoom || 12),
        withMarker: false,
        offset: Array.isArray(options?.offset) ? options.offset : [0, 0],
      });
      return;
    }
    const bounds = coordinates.reduce(
      (acc, coord) => acc.extend(coord),
      new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]),
    );
    const resolvedPadding =
      typeof options?.padding === "number"
        ? Number(options.padding || 80)
        : options?.padding &&
            typeof options.padding === "object" &&
            !Array.isArray(options.padding)
          ? options.padding
          : 80;
    map.fitBounds(bounds, {
      padding: resolvedPadding,
      maxZoom: Number(options?.maxZoom || 12),
      duration: Number(options?.duration || 800),
    });
    if (options?.withMarker === false) {
      clearLocationMarker();
    }
  }

  function getViewState() {
    // Snapshots current camera so it can be restored after modal/panel flows.
    const map = mapRef.current;
    if (!map) return null;
    const center = map.getCenter();
    return {
      center: [center.lng, center.lat],
      zoom: map.getZoom(),
      bearing: map.getBearing(),
      pitch: map.getPitch(),
    };
  }

  function restoreViewState(viewState) {
    // Restores previously saved camera without animation to keep UX predictable.
    const map = mapRef.current;
    if (!map || !viewState) return;
    map.jumpTo({
      center: viewState.center,
      zoom: viewState.zoom,
      bearing: viewState.bearing,
      pitch: viewState.pitch,
    });
  }

  useEffect(() => {
    // Map instance is created once per token availability and reused across UI toggles.
    if (!mapContainerRef.current || mapRef.current || !mapboxToken)
      return undefined;

    mapRef.current = createMapboxInstance({
      container: mapContainerRef.current,
      token: mapboxToken,
      style: selectedStyle,
    });

    const handleStyleLoad = () => {
      const restoreToken = styleRestoreTokenRef.current + 1;
      styleRestoreTokenRef.current = restoreToken;
      if (styleRestoreTimeoutRef.current) {
        window.clearTimeout(styleRestoreTimeoutRef.current);
      }
      const restoreDynamicMapState = (retries = 8) => {
        if (styleRestoreTokenRef.current !== restoreToken) return;
        const map = mapRef.current;
        if (!map || !map.isStyleLoaded() || !map.loaded()) {
          if (retries <= 0) return;
          styleRestoreTimeoutRef.current = window.setTimeout(
            () => restoreDynamicMapState(retries - 1),
            100,
          );
          return;
        }
        try {
          if (is3DEnabledRef.current) {
            applyMap3D(map, true);
          }
          ensureRouteLayer();
          ensureMeasurementLayers();
          syncMeasurementLayers(measurementPointsRef.current);
          ensureRouteShapePointLayer();
          syncRouteShapePointLayer(routeShapePointsRef.current || []);
          ensureCombinedRoutePreviewLayers();
          ensureBoundaryLayers();
          syncRouteGeometry(pointsRef.current || []);
          if (boundaryPointsRef.current.length >= 3) {
            drawBoundaryPolygonFromPoints(boundaryPointsRef.current);
          }
          const previewState = latestCombinedPreviewRef.current || {};
          if (previewState?.active) {
            drawCombinedRoutesPreview({
              routes: previewState?.routes || [],
              startPoints: previewState?.startPoints || [],
              syncDraftPoints: Boolean(previewState?.syncDraftPoints),
            });
          }
        } catch (error) {
          if (retries <= 0) return;
          styleRestoreTimeoutRef.current = window.setTimeout(
            () => restoreDynamicMapState(retries - 1),
            120,
          );
        }
      };
      let restoreStarted = false;
      const startRestore = () => {
        if (restoreStarted || styleRestoreTokenRef.current !== restoreToken) {
          return;
        }
        restoreStarted = true;
        if (styleRestoreTimeoutRef.current) {
          window.clearTimeout(styleRestoreTimeoutRef.current);
          styleRestoreTimeoutRef.current = null;
        }
        restoreDynamicMapState();
      };
      mapRef.current?.once("idle", startRestore);
      styleRestoreTimeoutRef.current = window.setTimeout(
        startRestore,
        1200,
      );
    };
    mapRef.current.on("style.load", handleStyleLoad);
    mapRef.current.on("click", (event) => {
      if (isMeasurementEnabledRef.current) {
        handleMeasurementMapClick(event);
        return;
      }
      if (isRouteShapeEditEnabledRef.current) {
        mapRef.current?.resize?.();
        const routeLngLat = resolveRouteShapeClickLngLat(event);
        if (routeLngLat) {
          onAddRouteShapePointRef.current?.(routeLngLat);
        }
        return;
      }
      // Point creation is gated by point-mode + drawing flags.
      if (!isPointModeRef.current || !isDrawingEnabledRef.current) return;
      const markerVariant = String(markerVariantRef.current || "");
      if (
        markerVariant === "mode-driving" ||
        markerVariant === "mode-walking"
      ) {
        const routeLngLat = resolveRouteShapeClickLngLat(event);
        if (routeLngLat) {
          addPointMarker(
            {
              lng: routeLngLat.longitude,
              lat: routeLngLat.latitude,
            },
            { insertOnRouteLine: true },
          );
          return;
        }
      }
      addPointMarker(event.lngLat);
    });

    return () => {
      styleRestoreTokenRef.current += 1;
      if (styleRestoreTimeoutRef.current) {
        window.clearTimeout(styleRestoreTimeoutRef.current);
        styleRestoreTimeoutRef.current = null;
      }
      mapRef.current?.off("style.load", handleStyleLoad);
      clearAllPoints();
      clearLocationMarker();
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      destroyMapboxInstance(mapRef.current);
      mapRef.current = null;
    };
  }, [mapboxToken]);

  useEffect(() => {
    setMapStyle(mapRef.current, selectedStyle);
  }, [selectedStyle]);

  // Terrain/extrusion is also style-dependent; re-sync when toggled.
  useEffect(
    () => sync3DWhenStyleReady(mapRef.current, is3DEnabled),
    [is3DEnabled],
  );
  useEffect(() => {
    // Keeps route paint in sync with reactive color/dash settings.
    const map = mapRef.current;
    if (!map) return;
    applyRouteStyleWithRetry();
  }, [routeColor, routeDashArray, selectedStyle]);

  useEffect(() => {
    // Re-applies route paint after external route payload changes.
    applyRouteStyleWithRetry();
  }, [externalRouteGeometry, selectedStyle]);

  useEffect(() => {
    // Syncs polyline geometry whenever points or external route source changes.
    syncRouteGeometry(points);
  }, [externalRouteGeometry, isRouteEnabled, points]);

  useEffect(() => {
    syncRouteShapePointMarkers(routeShapePoints);
    return () => {
      clearRouteShapeMarkers();
    };
  }, [routeShapePoints]);

  useEffect(() => {
    // Refreshes popup preview after point metadata changes, but avoids interrupting typing.
    if (!popupRefreshToken) return;
    if (!popupRef.current || !popupMarkerRef.current || !popupPointRef.current)
      return;
    const timeoutId = window.setTimeout(() => {
      const activeElement =
        typeof document !== "undefined" ? document.activeElement : null;
      const activeTag = String(activeElement?.tagName || "").toLowerCase();
      const isTypingElement =
        activeTag === "input" ||
        activeTag === "textarea" ||
        activeTag === "select" ||
        Boolean(activeElement?.closest?.(".ql-editor"));
      if (isTypingElement) return;
      openPointPopup(popupMarkerRef.current, popupPointRef.current);
    }, 700);
    return () => window.clearTimeout(timeoutId);
  }, [popupRefreshToken]);

  useEffect(() => {
    // Language changes should refresh open popup immediately to keep content in sync.
    if (!popupRef.current || !popupMarkerRef.current || !popupPointRef.current)
      return;
    openPointPopup(popupMarkerRef.current, popupPointRef.current);
  }, [popupLanguageRefreshKey]);

  useEffect(() => {
    // Keeps map viewport responsive inside dynamic panel/container layouts.
    if (!mapContainerRef.current || !mapRef.current) return undefined;

    const map = mapRef.current;
    const container = mapContainerRef.current;

    const resizeMap = () => {
      map.resize();
    };

    const timeoutId = window.setTimeout(resizeMap, 120);
    window.addEventListener("resize", resizeMap);

    let resizeObserver;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(() => resizeMap());
      resizeObserver.observe(container);
    }

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener("resize", resizeMap);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [mapboxToken]);

  return {
    mapContainerRef,
    selectedStyle,
    setSelectedStyle,
    is3DEnabled,
    setIs3DEnabled,
    isPointMode,
    setIsPointMode,
    points,
    setPointsFromList,
    removePointByIndex,
    removePointById,
    clearAllPoints,
    setMarkerColor,
    setMarkerVariant,
    isDrawingEnabled,
    setIsDrawingEnabled,
    routeColor,
    setRouteColor,
    routeDashArray,
    setRouteDashArray,
    isMeasurementEnabled,
    measurementDistanceKm,
    measurementPointCount: measurementPoints.length,
    toggleMeasurement,
    clearMeasurement,
    clearRouteGeometry,
    forceDrawExternalRoute,
    drawExternalRouteWithRetry,
    drawCombinedRoutesPreview,
    clearCombinedRoutePreview,
    drawBoundaryPolygonFromPoints,
    clearBoundaryPolygon,
    closeActivePointPopup,
    focusPointById,
    getViewState,
    restoreViewState,
    focusLocation,
    focusPointsBounds,
    clearLocationMarker,
    syncDrivingMarkerLabelsFromList,
    syncRandomMarkerIconsFromList,
  };
}

export default useMapboxDashboard;

